import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { usePhoneAuthAnalytics } from '../../../hooks/useAnalytics';
import MetricCard from '../core/MetricCard';
import AreaChart from '../../ui/AreaChart';
import PieChart from '../../ui/PieChart';
import CollapsibleSection from '../../ui/CollapsibleSection';
import { transformToChartData, transformToChartDataAsync } from '../../../services/chartService';
import { formatPercent, safeFormatFixed } from '../../../utils/format';
import { useFormatting } from '../../../hooks/useFormatting';
import { FILTER_PERIODS } from '../../../constants';

/**
 * Phone Authentication Metrics Component 🆕
 * Displays phone authentication-related analytics and statistics
 * @param {Object} props - Component props
 * @param {string} props.period - Time period for metrics
 * @param {boolean} [props.compact] - Whether to display in compact mode
 */
const PhoneAuthMetrics = ({ period, compact = false }) => {
  const { data: phoneAuthData, loading, error } = usePhoneAuthAnalytics(period);

  // Generate chart data for key metrics
  const [phoneUsersChart, setPhoneUsersChart] = useState(null);
  const [previousPhoneAuthData, setPreviousPhoneAuthData] = useState(null);

  // Fetch previous period data for accurate comparison
  useEffect(() => {
    const fetchPreviousPeriodData = async () => {
      try {
        const prevPeriodMap = {
          [FILTER_PERIODS.DAY]: FILTER_PERIODS.DAY,
          [FILTER_PERIODS.WEEK]: FILTER_PERIODS.WEEK,
          [FILTER_PERIODS.MONTH]: FILTER_PERIODS.MONTH,
          [FILTER_PERIODS.YEAR]: FILTER_PERIODS.YEAR,
          [FILTER_PERIODS.ALL]: FILTER_PERIODS.YEAR
        };
        
        const prevPeriod = prevPeriodMap[period] || FILTER_PERIODS.DAY;
        const { getPhoneAuthAnalytics } = await import('../../../services/firebaseService');
        const prevPhoneAuth = await getPhoneAuthAnalytics(prevPeriod).catch(() => null);
        setPreviousPhoneAuthData(prevPhoneAuth);
      } catch (error) {
        setPreviousPhoneAuthData(null);
      }
    };

    if (phoneAuthData) {
      fetchPreviousPeriodData();
    }
  }, [period, phoneAuthData]);

  useEffect(() => {
    if (!phoneAuthData) {
      setPhoneUsersChart(null);
      return;
    }

    const currentPhoneUsers = phoneAuthData.usersWithPhone || 0;
    const previousPhoneUsers = previousPhoneAuthData?.usersWithPhone || currentPhoneUsers * 0.9;
    
    Promise.all([
      transformToChartDataAsync(
        { usersWithPhone: currentPhoneUsers },
        { usersWithPhone: previousPhoneUsers },
        'usersWithPhone',
        period,
        {
          collectionName: 'users',
          valueExtractor: (docData) => (docData.phone || docData.primary_phone) ? 1 : 0
        }
      )
    ]).then(([usersChart]) => {
      setPhoneUsersChart(usersChart);
    }).catch(() => {
      setPhoneUsersChart(transformToChartData(
        { usersWithPhone: currentPhoneUsers },
        { usersWithPhone: previousPhoneUsers },
        'usersWithPhone',
        period
      ));
    });
  }, [phoneAuthData, previousPhoneAuthData, period]);

  // Get formatting functions from hook
  const { formatNumber } = useFormatting();

  // Memoize authentication method chart data
  const authMethodChartData = useMemo(() => {
    if (!phoneAuthData) return null;
    const totalUsers = phoneAuthData.totalUsers || 0;
    const phoneUsers = phoneAuthData.usersWithPhone || 0;
    const emailOnlyUsers = totalUsers - phoneUsers;
    
    if (totalUsers === 0) return null;
    
    return [
      { label: 'Phone Auth', value: phoneUsers },
      { label: 'Email Only', value: emailOnlyUsers }
    ].filter(item => item.value > 0);
  }, [phoneAuthData]);

  // Error state
  if (error) {
    return (
      <div className="phone-auth-metrics error">
        <h3>Error loading phone auth metrics</h3>
        <p>Please try refreshing the page</p>
      </div>
    );
  }

  // Normalize data for safe access
  const normalized = {
    totalUsers: phoneAuthData?.totalUsers || 0,
    usersWithPhone: phoneAuthData?.usersWithPhone || 0,
    usersWithVerifiedPhone: phoneAuthData?.usersWithVerifiedPhone || 0,
    usersWithEmailAndPhone: phoneAuthData?.usersWithEmailAndPhone || 0,
    newPhoneVerifications: phoneAuthData?.newPhoneVerifications || 0,
    phoneAuthAdoptionRate: phoneAuthData?.phoneAuthAdoptionRate || 0,
    phoneVerificationRate: phoneAuthData?.phoneVerificationRate || 0,
    dualAuthRate: phoneAuthData?.dualAuthRate || 0
  };

  return (
    <div className="phone-auth-metrics">
      <h2 className="section-title">
        <i className="bi bi-phone icon-brand" style={{ marginRight: 8 }}></i> Phone Authentication Metrics
      </h2>
      
      {/* Key Metrics */}
      <div className="metrics-grid-wrapper">
        <div className="metrics-grid">
          <div className="metric-group">
            <h3 className="group-title">Phone Auth Metrics</h3>
            <div className="metric-cards">
              <MetricCard
                title="Users with Phone"
                value={normalized.usersWithPhone}
                formatType="number"
                loading={loading}
                icon={<i className="bi bi-phone icon-brand"></i>}
                color="blue"
                subtitle={formatPercent(normalized.phoneAuthAdoptionRate, 1)}
              />
              <MetricCard
                title="Verified Phone Users"
                value={normalized.usersWithVerifiedPhone}
                formatType="number"
                loading={loading}
                icon={<i className="bi bi-patch-check icon-brand"></i>}
                color="green"
                subtitle={formatPercent(normalized.phoneVerificationRate, 1)}
              />
              <MetricCard
                title="Dual Auth Users"
                value={normalized.usersWithEmailAndPhone}
                formatType="number"
                loading={loading}
                icon={<i className="bi bi-shield-check icon-brand"></i>}
                color="purple"
                subtitle={formatPercent(normalized.dualAuthRate, 1)}
              />
              <MetricCard
                title="New Verifications"
                value={normalized.newPhoneVerifications}
                formatType="number"
                loading={loading}
                icon={<i className="bi bi-plus-circle icon-brand"></i>}
                color="teal"
                subtitle={`${period} period`}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Authentication Method Distribution Chart */}
      {authMethodChartData && (
        <div className={compact ? "chart-container-compact" : "chart-container"}>
          <PieChart
            data={authMethodChartData}
            label="Authentication Method Distribution"
            formatType="number"
            showPercentages={true}
            innerRadius={0.5}
          />
        </div>
      )}

      {/* All Other Metrics in Show More */}
      <CollapsibleSection
        title="Additional Phone Auth Metrics"
        initialVisible={2}
        defaultExpanded={false}
      >
        {[
          /* Adoption Rates */
          <div key="adoption" className="metric-group">
            <h3 className="group-title">Adoption Rates</h3>
            <div className="metric-cards">
              <MetricCard
                title="Phone Auth Adoption"
                value={safeFormatFixed(normalized.phoneAuthAdoptionRate, 1)}
                loading={loading}
                icon={<i className="bi bi-graph-up icon-brand"></i>}
                color="blue"
                subtitle="% of total users"
              />
              <MetricCard
                title="Phone Verification Rate"
                value={safeFormatFixed(normalized.phoneVerificationRate, 1)}
                loading={loading}
                icon={<i className="bi bi-check-circle icon-brand"></i>}
                color="green"
                subtitle="% of phone users"
              />
              <MetricCard
                title="Dual Auth Rate"
                value={safeFormatFixed(normalized.dualAuthRate, 1)}
                loading={loading}
                icon={<i className="bi bi-shield-check icon-brand"></i>}
                color="purple"
                subtitle="% of total users"
              />
            </div>
          </div>,

          // Charts in show more
          phoneUsersChart && (
            <div key="phone-users-chart" className={compact ? "chart-container-compact" : "chart-container"}>
              <AreaChart
                data={phoneUsersChart.data}
                comparisonData={phoneUsersChart.comparisonData}
                label="Phone Auth Users Over Time"
                formatType="number"
                color="blue"
              />
            </div>
          )
        ].filter(Boolean)}
      </CollapsibleSection>
    </div>
  );
};

PhoneAuthMetrics.propTypes = {
  period: PropTypes.string.isRequired,
  compact: PropTypes.bool
};

PhoneAuthMetrics.defaultProps = {
  compact: false
};

export default React.memo(PhoneAuthMetrics);

